import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  clearUserSession,
  getCached,
  getUserProfile,
  getUserStatus,
  setStoredUser,
} from "../services/api";
import { useAiMode } from "../context/AiModeContext";
import { LANDING_ONLY } from "../config/launchMode";

const normalizePlanName = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "premium") return "pro";
  if (normalized === "basic" || normalized === "pro" || normalized === "free") {
    return normalized;
  }
  return "free";
};

export default function ProtectedRoute({
  children,
  allowedPlans = null,
  upgradeRedirect = "/subscribe",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const { setMode } = useAiMode();
  const hasVerifiedRef = useRef(false);
  const interviewPlansLimit = 3;
  const isLandingOnly = LANDING_ONLY;
  const authRedirect = isLandingOnly ? "/" : "/login";

  const checkAccess = useCallback(async () => {
    const shouldBlock = !hasVerifiedRef.current;
    if (shouldBlock) {
      setChecking(true);
    }
    setError("");

    try {
      const profile = await getUserProfile({ force: shouldBlock });
      setStoredUser(profile, { reason: "bootstrap" });
      const userId = profile?.id || profile?._id;
      if (!userId) {
        await clearUserSession({ server: false, reason: "missing-profile" });
        hasVerifiedRef.current = false;
        navigate(authRedirect);
        return;
      }

      const shouldPrefetch =
        location.pathname === "/dashboard" ||
        location.pathname === "/interview-prep" ||
        location.pathname === "/interview-plans";

      if (shouldPrefetch) {
        getUserProfile({ ttl: 60000 }).catch(() => {});
        getCached(
          "/interview/plans",
          { params: { limit: interviewPlansLimit, summary: true } },
          { ttl: 60000 }
        ).catch(() => {});
      }

      const data = await getUserStatus({ ttl: 30000, force: shouldBlock });
      const { isVerified, isSubscribed, hasResumeData, needsResumeSetup, plan } = data;
      const normalizedPlan = normalizePlanName(plan);
      const hasPlanAccess = Boolean(isSubscribed) || normalizedPlan === "free";
      const normalizedAllowedPlans = Array.isArray(allowedPlans)
        ? allowedPlans.map((item) => normalizePlanName(item))
        : [];
      const requiresPlanUpgrade =
        normalizedAllowedPlans.length > 0 &&
        !normalizedAllowedPlans.includes(normalizedPlan);
      const shouldSetupResume =
        typeof needsResumeSetup === "boolean" ? needsResumeSetup : !hasResumeData;

      if (normalizedPlan !== "pro") {
        setMode("fast");
      }

      // ⛔ Block unverified users from anywhere except verify page
      if (!isVerified && location.pathname !== "/verify-email") {
        return navigate("/verify-email");
      }

      // ⛔ Force subscribe only when the account has no usable plan access.
      // Free is a valid tier and should be allowed into app routes.
      if (
        !hasPlanAccess &&
        !["/subscribe", "/billing", "/settings"].includes(location.pathname)
      ) {
        return navigate("/subscribe");
      }

      if (requiresPlanUpgrade) {
        return navigate(upgradeRedirect);
      }

      // ⛔ Force setup if resume data not available (unless already there)
      if (
        hasPlanAccess &&
        shouldSetupResume &&
        !["/setup-resume", "/billing", "/settings"].includes(location.pathname)
      ) {
        return navigate("/setup-resume");
      }

      // ✅ Passed all checks
      hasVerifiedRef.current = true;
      setChecking(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        void clearUserSession({ server: false, reason: "expired" });
        hasVerifiedRef.current = false;
        navigate(authRedirect);
        return;
      }
      if (!hasVerifiedRef.current) {
        const isNetworkFailure = !err?.response;
        setError(
          isNetworkFailure
            ? "Server is unreachable. Please try again in a moment."
            : "We couldn't verify your session. Please retry."
        );
        setChecking(false);
      }
    }
  }, [allowedPlans, authRedirect, location.pathname, navigate, setMode, upgradeRedirect]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  if (isLandingOnly) {
    return <Navigate to="/" replace />;
  }

  if (checking && !hasVerifiedRef.current) {
    return (
      <div className="min-h-[60vh] px-6 pt-24">
        <div className="mx-auto w-full max-w-6xl space-y-8">
          <div className="h-10 w-64 rounded-full bg-white/10 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            {[0, 1, 2, 3].map((idx) => (
              <div
                key={`stat-skeleton-${idx}`}
                className="h-24 rounded-2xl border border-gray-800 bg-[#171717] animate-pulse"
              />
            ))}
          </div>
          <div className="h-64 rounded-2xl border border-gray-800 bg-[#171717] animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center mt-12 text-center text-white">
        <p className="text-lg text-red-400 mb-4">{error}</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={checkAccess}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => navigate(authRedirect)}
            className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
          >
            {isLandingOnly ? "Back to Landing" : "Go to Login"}
          </button>
        </div>
      </div>
    );
  }

  return children;
}
